import { getConfig } from '@/server';
import {
  Router,
  type Request,
  type Response,
  type NextFunction,
  type Router as ExpressRouter,
} from 'express';
import {
  getRedirectUri,
  handleOAuthUserAuthentication,
  handleOAuthProviderLink,
  validateOAuthCode,
  type OAuthUserData,
  clearOAuthLinkCookie,
  validateOAuthStateAndGetMode,
  sendOAuthError,
  prepareOAuthInitiation,
  resolveMobileOutcomeFromCookie,
  toOAuthOutcome,
} from './oauthCommon';

interface GitHubTokenResponse {
  access_token: string;
  token_type: string;
  scope: string;
}

interface GitHubUserInfo {
  id: number;
  login: string;
  name: string;
  email: string | null;
  avatar_url?: string;
}

interface GitHubEmail {
  email: string;
  primary: boolean;
  verified: boolean;
  visibility: 'public' | 'private' | null;
}

async function exchangeCodeForToken(
  code: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<GitHubTokenResponse> {
  const tokenResponse = await fetch('https://github.com/login/oauth/access_token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      client_id: clientId,
      client_secret: clientSecret,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!tokenResponse.ok) {
    throw new Error(`Failed to exchange code for token: ${tokenResponse.statusText}`);
  }

  return tokenResponse.json();
}

async function fetchGitHubUserInfo(accessToken: string): Promise<GitHubUserInfo> {
  const userInfoResponse = await fetch('https://api.github.com/user', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/vnd.github.v3+json',
    },
  });

  if (!userInfoResponse.ok) {
    throw new Error(`Failed to fetch user info: ${userInfoResponse.statusText}`);
  }

  return userInfoResponse.json();
}

async function fetchGitHubUserEmails(accessToken: string): Promise<GitHubEmail[]> {
  const response = await fetch('https://api.github.com/user/emails', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/vnd.github.v3+json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user emails: ${response.statusText}`);
  }

  return response.json();
}

async function getGitHubUserEmail(
  githubUser: GitHubUserInfo,
  accessToken: string
): Promise<string | null> {
  if (githubUser.email) {
    return githubUser.email;
  }

  const emails = await fetchGitHubUserEmails(accessToken);
  return emails.find((e) => e.primary && e.verified)?.email ?? null;
}

async function handleGitHubAuthenticationCallback(req: Request, res: Response) {
  const code = validateOAuthCode(req.query.code);

  if (!code) {
    // No usable state to validate yet (a declined consent screen sends none),
    // but the state cookie still records where the app lives, so this can be
    // reported into the app instead of as JSON in the device browser.
    sendOAuthError(
      res,
      400,
      'Missing authorization code',
      resolveMobileOutcomeFromCookie(req, 'authStateGithub') ?? undefined,
      'missing_code'
    );
    return;
  }

  const stateResult = validateOAuthStateAndGetMode(req, res, 'authStateGithub');
  if (!stateResult) return;
  const { mode, linkedUserId } = stateResult;
  const outcome = toOAuthOutcome(stateResult);

  const githubClientId = String(getConfig('_system.user.auth.github.clientId'));
  const githubClientSecret = String(getConfig('_system.user.auth.github.clientSecret'));
  const redirectUri = getRedirectUri('github');

  try {
    // Exchange code for access token
    const tokenData = await exchangeCodeForToken(
      code,
      githubClientId,
      githubClientSecret,
      redirectUri
    );

    // Fetch user info
    const githubUser = await fetchGitHubUserInfo(tokenData.access_token);

    // Resolve a usable GitHub email (public email or fallback to primary verified email)
    const githubEmail = await getGitHubUserEmail(githubUser, tokenData.access_token);

    if (!githubEmail) {
      if (mode === 'link') {
        clearOAuthLinkCookie(res);
      }

      sendOAuthError(
        res,
        400,
        'Unable to retrieve a primary verified email from GitHub. Please ensure your GitHub account has a verified email set as primary.',
        outcome
      );
      return;
    }

    const nameParts = githubUser.name ? githubUser.name.trim().split(/\s+/) : [];
    const firstName = nameParts[0] || undefined;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;

    const userData: OAuthUserData = {
      id: String(githubUser.id),
      email: githubEmail,
      emailVerified: true, // Assume public email is verified
      providerName: 'github',
      firstName,
      lastName,
      avatarUrl: githubUser.avatar_url || undefined,
    };

    if (mode === 'link') {
      await handleOAuthProviderLink(req, res, userData, linkedUserId, outcome);
    } else {
      await handleOAuthUserAuthentication(req, res, userData, outcome);
    }
  } catch (error) {
    console.error('GitHub OAuth error:', error);
    if (mode === 'link') {
      clearOAuthLinkCookie(res);
    }
    sendOAuthError(res, 500, 'Authentication failed', outcome);
  }
}

function getRouter(): ExpressRouter {
  const githubAuthRouter = Router();

  // Middleware to check if GitHub auth is enabled and configured
  const checkGitHubEnabled = (_req: Request, res: Response, next: NextFunction) => {
    const githubEnabled = Boolean(getConfig('_system.user.auth.github.enabled'));
    const githubClientId = String(getConfig('_system.user.auth.github.clientId'));
    const githubClientSecret = String(getConfig('_system.user.auth.github.clientSecret'));

    if (!githubEnabled || !githubClientId || !githubClientSecret) {
      sendOAuthError(res, 503, 'GitHub authentication is not configured', {
        platform: 'api',
      });
      return;
    }

    next();
  };

  // Initiate OAuth flow
  githubAuthRouter.get(
    '/api/_internal/auth/github',
    checkGitHubEnabled,
    async (req: Request, res: Response) => {
      const githubClientId = String(getConfig('_system.user.auth.github.clientId'));
      const redirectUri = getRedirectUri('github');
      const githubScopes = getConfig('_system.user.auth.github.scopes');
      const scopes = githubScopes
        ? String(githubScopes)
            .split(',')
            .map((s) => s.trim())
            .join(' ')
        : 'user:email';

      const initiation = await prepareOAuthInitiation(req, res, 'authStateGithub');
      if (!initiation) return;
      const { state } = initiation;

      const authUrl = new URL('https://github.com/login/oauth/authorize');
      authUrl.searchParams.append('client_id', githubClientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('scope', scopes);
      authUrl.searchParams.append('state', state);

      res.redirect(authUrl.toString());
    }
  );

  // Handle OAuth callback
  githubAuthRouter.get(
    '/api/_internal/auth/github/callback',
    checkGitHubEnabled,
    handleGitHubAuthenticationCallback
  );

  return githubAuthRouter;
}

export default getRouter;
